import React from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import Drawer from "@material-ui/core/Drawer";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import List from "@material-ui/core/List";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Badge from "@material-ui/core/Badge";
import Link from "@material-ui/core/Link";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Divider from "@material-ui/core/Divider";
import MenuIcon from "@material-ui/icons/Menu";
import { MainListItems, SecondaryListItems } from '../../components/menu';
import NotificationsIcon from '@material-ui/icons/Notifications';
import ExitToAppIcon from '@material-ui/icons/PowerSettingsNew';
import njclogo from '../../assets/icons/intelog_new_png.png';
import { usePermissions } from '../../context/permissionContext';
import { fullLogout } from '../../auth/oidc.js';


import './header.scss';
import { useNavigate } from "react-router-dom";

const drawerWidth = 260;

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex"
  },
  toolbar: {
    paddingRight: 24 // keep right padding when drawer closed
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0 8px",
    ...theme.mixins.toolbar
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  menuButton: {
    marginRight: 36
  },
  menuButtonHidden: {
    display: "none"
  },
  title: {
    flexGrow: 1
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9)
    }
  },
  appBarSpacer: {
    ...theme.mixins.toolbar,
    minHeight: '56px !important'
  },
  content: {
    flexGrow: 1,
    height: "100vh",
    overflow: "auto"
  },
  container: {
    paddingTop: 0,
    paddingBottom: theme.spacing(4)
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column"
  },
  fixedHeight: {
    height: 240
  }
}));

export default function CustomHeader() {
  let navigate = useNavigate();
  const classes = useStyles();
  const [open, setOpen] = React.useState(true);
  const [profileAnchor, setProfileAnchor] = React.useState(null);
  const { logout } = usePermissions();

  const storedUserData = JSON.parse(localStorage.getItem('user') || '{}');
  const initials = (storedUserData.username || 'U').substring(0, 2).toUpperCase();

  const handleDrawerOpen = () => { setOpen(true); };
  const handleDrawerClose = () => { setOpen(false); };
  const handleLogout = () => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (storedUser?.id) {
      localStorage.removeItem(`smarthub_state_${storedUser.id}`);
      localStorage.removeItem('langraph_session_id');
    }
    logout();
    // fullLogout clears Intelog storage and, for Keycloak logins, ends the Keycloak
    // SSO session too (then redirects to "/"). Password logins just go to "/".
    fullLogout();
  }

  const fixedHeightPaper = clsx(classes.paper, classes.fixedHeight);

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar
        position="absolute"
        className={clsx(classes.appBar, open && classes.appBarShift)}
      >
        <Toolbar className={classes.toolbar}>

          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            className={clsx(
              classes.menuButton,
              open && classes.menuButtonHidden
            )}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            className={classes.title}
          >
          </Typography>
          <IconButton color="inherit" className="header-icon-button">
            <Badge>
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <IconButton color="inherit" onClick={e => setProfileAnchor(e.currentTarget)} className="header-icon-button">
            <div className="header-avatar">{initials}</div>
          </IconButton>
          <Menu
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={() => setProfileAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            getContentAnchorEl={null}
            PaperProps={{ className: 'header-profile-menu' }}
          >
            <div className="header-profile-info">
              <div className="header-profile-name">{storedUserData.username}</div>
              <div className="header-profile-email">{storedUserData.email}</div>
            </div>
            <Divider />
            <MenuItem onClick={() => { setProfileAnchor(null); handleLogout(); }} className="header-logout-item">
              <ExitToAppIcon fontSize="small" style={{ marginRight: 8 }} /> Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        className="customDrawer"
        classes={{
          paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose)
        }}
        open={open}
      >
        <div className="logo-container">
          <img
            className="intelog-icon components--logo-img__3108zO"
            alt="INTELOG"
            src={njclogo}
          />
        </div>
        <div className="menu-list-items">
          <List><MainListItems></MainListItems></List>
          <List><SecondaryListItems></SecondaryListItems></List>
        </div>
      </Drawer>

    </div >
  );
}

export  function CustomHeaderWithOutAppBar() {
  let navigate = useNavigate();
  const classes = useStyles();
  const [open, setOpen] = React.useState(true);
    const { logout } = usePermissions(); 


  const handleDrawerOpen = () => {
    setOpen(true);
  };
  const handleDrawerClose = () => {
    setOpen(false);
  };
  const handleLogout = () => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (storedUser?.id) {
      localStorage.removeItem(`smarthub_state_${storedUser.id}`);
      localStorage.removeItem('langraph_session_id');
    }
    logout();
    // fullLogout clears Intelog storage and, for Keycloak logins, ends the Keycloak
    // SSO session too (then redirects to "/"). Password logins just go to "/".
    fullLogout();
  }

  const fixedHeightPaper = clsx(classes.paper, classes.fixedHeight);

  return (
    <div className={classes.root}>
      <CssBaseline />
    
      <Drawer
        variant="permanent"
        className="customDrawer"
        classes={{
          paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose)
        }}
        open={open}
      >
        <div className="logo-container">
          <img
            className="intelog-icon components--logo-img__3108zO"
            alt="INTELOG"
            src={njclogo}
          />
        </div>
        <div className="menu-list-items">
          <List><MainListItems></MainListItems></List>
          <List><SecondaryListItems></SecondaryListItems></List>
        </div>
      </Drawer>

    </div >
  );
}

